/**
 * Everything the page shows, worked out from one reading of
 * `public.menu_context()` and the customer's answers.
 *
 * This is vB6's `cal()`, `avail()`, `ranked()`, `limits()`, `ensureSel()`,
 * `build()`, `totals()` and `rules()`, with two changes and no others:
 *
 *  1. **the quantity model is `packages/engine`.** vB6 carried its own copy
 *     of §6.7 — `sideLines`, the gluten-free redistribution, the
 *     production-unit rounding — and its own catalogue, parameters, holidays
 *     and postcode table. All of it is gone: the figures come from the
 *     register, the catalogue from the publication, the calendar from
 *     `public.calendar_days`, and the arithmetic from `priceOrder()`, which
 *     is the same function `submit_order` re-runs on the server;
 *  2. **the demo switches are gone.** `?photos=`, `?overview=`, `?new=`,
 *     `?snc=`, `?dayp=`, `?dayo=` and `?dayc=` stood in for data that now
 *     exists: `menu_show_photos` and `menu_show_overview` are register keys,
 *     and the capacity of §11.3 and §11.4 comes back from the server as an
 *     answer. The one they stood in for that still has no answer here is
 *     §11.4's *first order from this email or VAT number*, which the browser
 *     cannot know; the server decides it, and the page says the same sentence
 *     either way. Logged for Barna.
 *
 * §19.2 principle 2 governs the lot: what this file works out is display
 * only.
 */

import {
  CONFERENCE,
  CONFERENCE_BREAK_OCCASION,
  coffeeOfferOf,
  coffeeParamsFrom,
  conferenceElements,
  conferenceSelectionFrom,
  deliveryLine,
  dishCountSuggestion,
  dishFrom,
  engineParamsFrom,
  fromDecimal,
  netOfGross,
  earlyPickupNotice,
  minimumBasisGross,
  occasionFrom,
  orderTotal,
  preselectCount,
  rankForPreselection,
  refreshmentDrinkQuantity,
  serviceLine,
  sideOfCategory,
  summaryView,
  toNumber,
  type Balance,
  type CoffeeOffer,
  type CoffeeServings,
  type ConferenceSelection,
  type DeliveryZone,
  type Dish,
  type DishCountSuggestion,
  type DrinkSelection,
  type EngineParams,
  type Occasion,
  type OccasionRow,
  type OrderInput,
  type OrderTotal,
  type PricedOrder,
  type PublishedDish,
  type ServiceLine,
  type Side,
  type SummaryView,
} from '@plt/engine'
import { priceOrder } from '@plt/engine'

import type { MenuContext, MenuGroupRow, SlotRow } from '../lib/menuContext.ts'
import { OCCASION_LEADING_DISHES } from './catalogue.ts'
import type { Lang } from './lang.ts'
import type { Filter, PageState, Selection } from './state.ts'
import { guestsOf } from './state.ts'

/* ------------------------------------------------------------------ *
 * The catalogue as the page reads it
 * ------------------------------------------------------------------ */

/** One published dish, with the engine's view of it beside the page's. */
export interface MenuDish {
  id: string
  nameHu: string
  nameEn: string
  /** The catalogue's own Hungarian category word, which labels the row. */
  category: string
  /** The §5.3 page group, out of the register's `menu_group_map`. */
  group: string
  /** `sav`, `sw`, or null for fruit and drinks, which sit outside both. */
  side: Side | null
  isDrink: boolean
  isFruit: boolean
  /** The numbered allergens of §5.3. Example data until §7.1 is signed off. */
  allergens: number[]
  /**
   * `build/FIXES.md` item 89 (P10): true when all fourteen allergen fields
   * hold `true` or `false`. The publication refuses a dish that fails this,
   * so it is true on every published row; it travels to the page so that the
   * card can never print *nincs* over a null if one ever reaches it by
   * another route.
   */
  allergensComplete: boolean
  /** Read out of the publication, never worked out again (§5.3, §6.7 step 6). */
  glutenFreeEligible: boolean
  featured: boolean
  cafe: boolean
  /** §8.3: can this dish still be made for this date? */
  leadTimeOk: boolean
  /** DR-17: true while the allergen block is a test marking, not Robi's. */
  allergenTestMarked: boolean
  /** §7.1's publication gate, as the publication reports it. */
  allergenStatus: string | null
  /**
   * `build/FIXES.md` item 74 (F3): the volume one catalogue unit of a drink
   * holds, in litres. **Null everywhere today** — the catalogue has no such
   * figure on any row — and that is the *needed* value F3 waits on, not a
   * gap to fill in. While it is null the refreshment-drink line stays hidden.
   */
  unitVolumeLitres: number | null
  /**
   * Run 10 items 7b and 7c: the publication contract's
   * `plant_based_lookalike_hu` and `_en` — a name that reads like an animal
   * product, per language, because the terms that find it are per language.
   * Shown in italics with an asterisk and one footnote per view, on customer
   * views only. Read through `lookalikeIn`.
   */
  plantBasedLookalike: { hu: boolean; en: boolean }
  /**
   * Run 10 item 7c: where the shown description italicises a look-alike
   * term, as 0-based [start, end) pairs, per language.
   */
  descriptionMarks: { hu: Array<[number, number]>; en: Array<[number, number]> }
  /** Run 12 item F: where the name is marked, per language. */
  nameMarks: { hu: Array<[number, number]>; en: Array<[number, number]> }
  /**
   * Run 11 item 3: the publication contract's `shown_description_hu` and
   * `_en` — the approved draft, else the catalogue text, else null. Printed
   * as one muted line under the name on menu cards, never on the offer card.
   */
  shownDescription: { hu: string | null; en: string | null }
  /**
   * Dish photos (DECIDED 20261101): the publication's `photo` — the 400 and
   * 800 squares, the large file for the zoom, and its size — or null, and the
   * drawn icon is shown. Read from the publication and from nothing else.
   */
  photo: DishPhoto | null
  /** Null for a dish with no price, which cannot be ordered or priced. */
  engine: Dish | null
  raw: PublishedDish
}

export interface DishPhoto {
  /** The 400 square; kept under this name for compatibility. */
  thumb: string
  /** The 800 square the dish card shows. */
  card: string
  /** Longest side 1 200 px, for the zoom. */
  large: string
  w: number | null
  h: number | null
}

/** The publication's photo, or null when it carries none or a malformed one. */
export function photoOf(value: unknown): DishPhoto | null {
  if (value === null || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (typeof v['large'] !== 'string' || v['large'] === '') return null
  const text = (key: string) => (typeof v[key] === 'string' && v[key] !== '' ? (v[key] as string) : (v['large'] as string))
  const size = (key: string) => (typeof v[key] === 'number' ? (v[key] as number) : null)
  return { thumb: text('thumb'), card: text('card'), large: v['large'] as string, w: size('w'), h: size('h') }
}

export interface MenuGroup {
  key: string
  side: string
  nameHu: string
  nameEn: string
  sortOrder: number
}

const name = (row: PublishedDish): { hu: string; en: string } => ({
  hu: row.name_hu ?? row.id,
  en: row.name_en ?? row.name_hu ?? row.id,
})

export function toMenuDish(row: PublishedDish): MenuDish {
  const side = sideOfCategory(row.price_category)
  const names = name(row)
  const extra = row as PublishedDish & {
    allergens?: number[]
    allergens_complete?: boolean | null
    allergen_test_marked?: boolean | null
    allergen_status?: string | null
    unit_volume_l?: number | string | null
    plant_based_lookalike?: boolean | null
    plant_based_lookalike_hu?: boolean | null
    plant_based_lookalike_en?: boolean | null
    description_hu_marks?: unknown
    name_hu_marks?: unknown
    name_en_marks?: unknown
    description_en_marks?: unknown
    shown_description_hu?: string | null
    shown_description_en?: string | null
    photo?: unknown
  }
  return {
    id: row.id,
    nameHu: names.hu,
    nameEn: names.en,
    category: row.category ?? '',
    group: row.menu_group ?? '',
    side,
    isDrink: row.is_drink === true,
    isFruit: row.price_category === 'gyumolcs',
    allergens: extra.allergens ?? [],
    allergensComplete: extra.allergens_complete === true,
    glutenFreeEligible: row.gluten_free_eligible === true,
    featured: row.featured === true,
    cafe: row.cafe === true,
    leadTimeOk: row.lead_time_ok !== false,
    photo: photoOf(extra.photo),
    allergenTestMarked: extra.allergen_test_marked === true,
    allergenStatus: extra.allergen_status ?? null,
    unitVolumeLitres: litres(extra.unit_volume_l),
    plantBasedLookalike: {
      hu: (extra.plant_based_lookalike_hu ?? extra.plant_based_lookalike) === true,
      en: (extra.plant_based_lookalike_en ?? extra.plant_based_lookalike) === true,
    },
    descriptionMarks: { hu: marks(extra.description_hu_marks), en: marks(extra.description_en_marks) },
    // Run 12 item F: where the name is marked; never the whole name.
    nameMarks: { hu: marks(extra.name_hu_marks), en: marks(extra.name_en_marks) },
    shownDescription: { hu: text(extra.shown_description_hu), en: text(extra.shown_description_en) },
    engine: dishFrom(row),
    raw: row,
  }
}

/** Run 12 item F: whether the dish name, in the language shown, carries a
 *  marked expression (and so an asterisk). */
export const lookalikeIn = (dish: MenuDish | undefined, lang: Lang): boolean =>
  dish !== undefined && dish.nameMarks[lang].length > 0

/** Run 12 item F: where to mark the name in the language shown. */
export const nameMarksIn = (dish: MenuDish | undefined, lang: Lang): ReadonlyArray<readonly [number, number]> =>
  dish === undefined ? [] : dish.nameMarks[lang]

/** Run 12 item 4c: whether the description a menu card shows in this language
 *  carries a marked term, and so an asterisk. */
export const descriptionMarkedIn = (dish: MenuDish | undefined, lang: Lang): boolean =>
  dish !== undefined && dish.shownDescription[lang] !== null && dish.descriptionMarks[lang].length > 0

/** Run 11 item 3: a contract text, blank read as none. */
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

/** Run 11 item 3: the description a menu card shows in a language, or null. */
export const shownDescriptionIn = (dish: MenuDish, lang: Lang): string | null => dish.shownDescription[lang]

/** The contract's description marks, keeping only well-formed pairs. */
const marks = (value: unknown): Array<[number, number]> =>
  Array.isArray(value)
    ? value.filter(
        (pair): pair is [number, number] =>
          Array.isArray(pair) &&
          pair.length === 2 &&
          Number.isInteger(pair[0]) &&
          Number.isInteger(pair[1]) &&
          pair[0] >= 0 &&
          pair[1] > pair[0],
      )
    : []

/** A catalogue volume as a number, or null where there is none to read. */
const litres = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null
  const parsed = Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const groupsOf = (rows: readonly MenuGroupRow[]): MenuGroup[] =>
  rows.map((row) => ({
    key: row.group_key,
    side: row.side,
    nameHu: row.name_hu ?? row.group_key,
    nameEn: row.name_en ?? row.name_hu ?? row.group_key,
    sortOrder: row.sort_order,
  }))

/* ------------------------------------------------------------------ *
 * The calendar (§8), as the server answered it
 * ------------------------------------------------------------------ */

export type Lane = 'std' | 'short' | 'closed'

export interface Calendar {
  /** True when a date has been chosen and the calendar knows it. */
  ok: boolean
  /** The reason it is not ok, where the server gave one. */
  reason: string | null
  eventDate: Date | null
  now: Date
  lane: Lane
  weekend: boolean
  monday: boolean
  holiday: boolean
  clash: boolean
  /** §8.1: the day a weekend event is collected. */
  handoverDate: Date | null
  freezeAt: Date | null
  /**
   * Run 11 item 5: the transfer payment deadline as the server works it out
   * from now — díjbekérő + payment_window_bd business days, capped at the
   * order deadline (`public.transfer_payment_deadline`). Before the
   * díjbekérő exists, now is when it would be issued.
   */
  transferPaymentDeadline: Date | null
  /**
   * redesign_payment_photos (20261101): the short-notice order deadline,
   * E − short_notice_bd at freeze_time, which caps a short-notice order's
   * díjbekérő deadline under the díjbekérő route.
   */
  shortNoticeDeadline: Date | null
  businessDaysLeft: number
  /** §11.8: inside `fee_window_h` of the start. */
  insideFeeWindow: boolean
  startAt: Date | null
}

const parseDate = (value: string | null | undefined): Date | null =>
  value === null || value === undefined || value === '' ? null : new Date(value)

/** §5.2: the event date at midday, so a time zone cannot move it a day. */
const eventDateOf = (iso: string): Date | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00`) : null

export function calendarOf(context: MenuContext, state: PageState, slots: SlotRow[]): Calendar {
  const now = parseDate(context.now) ?? new Date()
  const eventDate = eventDateOf(state.date)
  const window = context.window
  const ok = context.ok && eventDate !== null && window !== null
  const laneWindow = window?.lane_window ?? 'standard'
  const lane: Lane =
    laneWindow === 'standard' ? 'std' : laneWindow === 'short_notice' ? 'short' : 'closed'
  const weekday = eventDate?.getDay() ?? -1
  const startAt =
    eventDate === null ? null : new Date(`${state.date}T${state.start.slice(0, 5)}`)
  const feeWindowHours = Number(context.params['fee_window_h'] ?? 0)
  const handover = slots[0]?.slot_date
  return {
    ok,
    reason: context.reason ?? null,
    eventDate,
    now,
    lane,
    weekend: weekday === 0 || weekday === 6,
    monday: weekday === 1,
    holiday: (context.calendar?.holiday_name ?? null) !== null || context.calendar?.closed === true,
    clash: context.calendar?.is_clash_date === true,
    handoverDate: handover === undefined ? eventDate : eventDateOf(handover),
    freezeAt: parseDate(context.deadlines?.['card']?.freeze_at ?? null),
    transferPaymentDeadline: parseDate(context.deadlines?.['transfer']?.payment_deadline ?? null),
    shortNoticeDeadline: parseDate(window?.short_notice_deadline ?? null),
    businessDaysLeft: window?.business_days_left ?? 0,
    insideFeeWindow:
      startAt !== null && now.getTime() >= startAt.getTime() - feeWindowHours * 3_600_000,
    startAt,
  }
}

/** §5.2: only the slots that end by the start time are offered. */
export const offeredSlots = (context: MenuContext): SlotRow[] =>
  context.slots.filter((slot) => slot.offered)

/** The window as the customer reads it: *08:30–10:30*. */
export const slotWindow = (slot: SlotRow): string =>
  `${slot.starts_at.slice(0, 5)}–${slot.ends_at.slice(0, 5)}`

/**
 * Run 16 item 2 (DECIDED 20261015, Barna): the collection window as §12
 * states it — `pickup_from` to `pickup_to`, and `monday_from` to `pickup_to`
 * on a Monday. Read from the register, never from a literal, which is what
 * item 7 asks of every place a pickup window is shown.
 */
export const pickupWindow = (param: (key: string) => string, monday: boolean): string => {
  const from = String(monday ? param('monday_from') : param('pickup_from')).trim()
  const to = String(param('pickup_to')).trim()
  return from === '' || to === '' ? '' : `${from}–${to}`
}

/**
 * Run 16 item 2: the window the food changes hands in, whichever way the
 * customer chose — the delivery slot they picked, or the collection window.
 * Empty when there is none, which §5.2 allows and which prints the date alone.
 */
export function fulfilmentWindow(
  state: PageState,
  slots: SlotRow[],
  calendar: Calendar,
  param: (key: string) => string,
): string {
  if (state.fulfilment === 'pickup' || calendar.weekend) {
    return pickupWindow(param, calendar.monday && !calendar.weekend)
  }
  const chosen = slots.find((slot) => slot.slot_id === state.slotId) ?? slots[slots.length - 1]
  return chosen === undefined ? '' : slotWindow(chosen)
}

/**
 * Run 16 item 2: the day the food changes hands — the event day, or the
 * collection day for a weekend event (§8.1, the business day before).
 */
export const fulfilmentDate = (state: PageState, calendar: Calendar): Date | null =>
  calendar.weekend || state.fulfilment === 'pickup'
    ? (calendar.handoverDate ?? calendar.eventDate)
    : calendar.eventDate

/* ------------------------------------------------------------------ *
 * Run 19 §8: a pickup that is not on the event date
 * ------------------------------------------------------------------ */

/** A `Date` the page holds, as the `YYYY-MM-DD` the engine's formatters take.
 *  The page builds its dates at midday local time (`eventDateOf`), so the
 *  local calendar fields are the Budapest ones and nothing is converted. */
export const isoDateOf = (date: Date | null): string | null => {
  if (date === null || Number.isNaN(date.getTime())) return null
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * **Run 19 §8 (DECIDED 20261016, Barna).** The sentence a pickup carries when
 * the food changes hands on a day other than the event date — a weekend
 * event, today, and any other rule that moves the collection tomorrow.
 *
 * `null` where there is nothing to say: a delivery, a collection on the day,
 * or a date the page cannot read yet. The words themselves are
 * `earlyPickupNotice` in `@plt/engine`, which the emails, the PDFs, the
 * kitchen output and the console read too, so no surface can word it
 * differently from another.
 */
export function earlyPickupLine(args: {
  state: PageState
  calendar: Calendar
  param: (key: string) => string
  lang: Lang
}): string | null {
  const { state, calendar, param, lang } = args
  const pickupDate = isoDateOf(fulfilmentDate(state, calendar))
  const eventDate = isoDateOf(calendar.eventDate)
  return earlyPickupNotice(lang, { pickupDate, eventDate, pickupTo: param('pickup_to') })
}

/**
 * `build/FIXES.md` item 32. Delivery is blocked when the customer asked for
 * it and no slot of §5.2 ends by the start time — the same condition the
 * order is blocked on, the note is printed for and the fee is withheld by.
 * One function, so nothing that reads it can drift away from the blocker.
 */
export const deliveryBlocked = (state: PageState, slots: SlotRow[]): boolean =>
  state.fulfilment !== 'pickup' && slots.length === 0

/**
 * Specification §3.7 (DECIDED 20261023): the postcode is beyond the delivery
 * radius and outside Budapest, so delivery is not offered there. The answer is
 * the server's (`public.postcode_delivery`, carried by `menu_context`); the
 * page only reads it. Pickup stays open and the customer chooses it — the page
 * never switches the order over by itself.
 */
export const outsideDeliveryRadius = (context: MenuContext): boolean =>
  context.delivery?.deliverable === false

/** §3.7's sentence, with `delivery_max_km` for {max}. */
export function outsideRadiusSentence(
  t: (hu: string, en: string) => string,
  context: MenuContext,
): string {
  const max = context.delivery?.max_km
  const shown = max === null || max === undefined ? '' : String(max)
  return t(
    `Kiszállítást jelenleg a Graphisoft Parktól ${shown.replace('.', ',')} km-es körzetben vállalunk. Távolabbi helyszínre a rendelést személyesen átveheti a Graphisoft Parkban.`,
    `We currently deliver within ${shown} km of Graphisoft Park. For venues further away, you can collect your order from Graphisoft Park.`,
  )
}

/* ------------------------------------------------------------------ *
 * The published range for the date (§8.3)
 * ------------------------------------------------------------------ */

/**
 * §8.3: on the short-notice lane the range is the café dishes that can still
 * be made in the business days left. On the standard lane it is the whole
 * publication. A dish with no price is not offered at all: it cannot be
 * priced and §5.11's category row would be wrong without it.
 */
export function availableDishes(dishes: readonly MenuDish[], lane: Lane): MenuDish[] {
  const priced = dishes.filter((dish) => dish.side !== null && dish.engine !== null)
  if (lane !== 'short') return priced
  return priced.filter((dish) => dish.cafe && dish.leadTimeOk)
}

export function availableFruit(dishes: readonly MenuDish[], lane: Lane): MenuDish[] {
  const priced = dishes.filter((dish) => dish.isFruit && dish.raw.net_price !== null)
  if (lane !== 'short') return priced
  return priced.filter((dish) => dish.cafe && dish.leadTimeOk)
}

export const drinkDishes = (dishes: readonly MenuDish[]): MenuDish[] =>
  dishes.filter((dish) => dish.isDrink)

/**
 * The allergen line on a card and in the Összesítés, written once.
 *
 * Verified means all of it: the publication's status is `verified`, the
 * block is not a test marking (DR-17, `build/FIXES.md` item 20) and all
 * fourteen allergen fields hold `true` or `false` (`build/FIXES.md` item 89,
 * catalogue ruling 1 of 9 October 2026). A missing or unknown flag is not
 * verified. `docs/overnight_constraints.md` §8 and `CLAUDE.md` both say example
 * allergen data must never read as verified.
 *
 * Only a verified dish prints its list, and only a verified dish with an empty
 * list prints *nincs*. Every other dish reads *Allergének ellenőrzés alatt*,
 * with no list beside it: Barna's instruction of 10 October 2026 for the test
 * site, where `CATALOGUE_ALLOW_UNVERIFIED` publishes dishes Robi has not yet
 * verified. A value nobody has vouched for is not shown as if it were one, and
 * *nincs* over an unverified block would be a missing value read as "does not
 * contain".
 */
export const allergensAreVerified = (dish: MenuDish): boolean =>
  dish.allergenStatus === 'verified' && !dish.allergenTestMarked && dish.allergensComplete

export const ALLERGENS_BEING_VERIFIED = {
  hu: 'Allergének ellenőrzés alatt',
  en: 'Allergens being verified',
} as const

export type AllergenLine =
  | { verified: true; allergens: readonly number[] }
  | { verified: false }

export const allergenLine = (dish: MenuDish | undefined): AllergenLine =>
  dish !== undefined && allergensAreVerified(dish)
    ? { verified: true, allergens: dish.allergens }
    : { verified: false }

/* ------------------------------------------------------------------ *
 * The occasion, the parameters and the pre-selection
 * ------------------------------------------------------------------ */

export function engineParams(context: MenuContext): EngineParams {
  return engineParamsFrom(context.params, context.zones)
}

/**
 * §6.1 with §5.3's pre-selection rank attached. The two points and the §6.6
 * range come from `public.occasions`; the leading dishes are the page's and
 * are Balázs's to set (`catalogue.ts`).
 *
 * `build/FIXES.md` item 73 (F2) gave one occasion no points of its own: the
 * all-day conference is *(welcome + breaks) × Kávészünet + (lunch ?
 * Munkaebéd : 0)*, read out of the other rows at run time. So the whole
 * reading and the register go to `occasionFrom`, not just the one row, and
 * `selection` carries the three answers. Omitted, it is F2's PROPOSED day —
 * welcome yes, two breaks, lunch no — which is what the page opens on and
 * what every other occasion ignores. The controls that set it are `081`'s.
 */
export function occasionOf(
  context: MenuContext,
  key: string,
  selection?: Partial<ConferenceSelection> | null,
): Occasion | null {
  const row = context.occasions.find((occasion) => occasion.key === key)
  if (row === undefined) return null
  const occasions: Record<string, OccasionRow> = Object.fromEntries(
    context.occasions.map((occasion) => [occasion.key, occasion]),
  )
  return {
    ...occasionFrom(row, { occasions, params: context.params, conference: selection }),
    leadingDishes: OCCASION_LEADING_DISHES[key],
  }
}

/* ------------------------------------------------------------------ *
 * The conference drinks: F3's refreshment drink and F7's coffee
 * ------------------------------------------------------------------ */

/**
 * `build/FIXES.md` item 78 (F7): the coffee question is *shown only when the
 * occasion is Kávészünet or the conference occasion*. Both keys come from the
 * engine, which already names them for F2.
 */
export const COFFEE_OCCASIONS: readonly string[] = [CONFERENCE_BREAK_OCCASION, CONFERENCE]

/**
 * **Run 19 §16 (EXPERT, Balázs 20261016; the two markups DECIDED 20261018,
 * Barna): coffee goes live.**
 *
 * Run 16 item 8's two PROVISIONAL figures — 0,8 and 0,5 cups per guest per
 * break — are replaced by Balázs's own, the midpoints of the ranges he
 * gave: **1,15 cups per guest at a morning serving and 0,65 at an afternoon
 * one**. Any coffee counts as one cup: espresso, latte or anything else out
 * of the machine.
 *
 * `coffee_per_head_morning`, `coffee_per_head_afternoon` and
 * `coffee_per_head_per_occasion` keep their rows and their place on the
 * browser allow-list, marked RETIRED, so the register still says out loud
 * which answer used to be provisional. **Nothing reads them any more**: the
 * eleven figures §16 gives are read together by `coffeeParamsFrom` in
 * `@plt/engine`, which answers null where any one of them is missing.
 */
export const COFFEE_CUPS_AM_PARAM = 'coffee_cups_am'
export const COFFEE_CUPS_PM_PARAM = 'coffee_cups_pm'

/** Run 16 item 8: the clock the two figures are split at. */
export const COFFEE_AFTERNOON_FROM = '12:00'

/** §12: F3's 2 dl per head per occasion, which ruling 3 states and §12 holds. */
export const REFRESHMENT_LITRES_PARAM = 'refreshment_l_per_head_per_occasion'

/**
 * How many occasions the day holds, for the two items that count occasions
 * rather than pieces. F7: *1 for Kávészünet; welcome + breaks + lunch for a
 * conference*. F3 counts the same elements. Zero for every other occasion,
 * which is what hides both lines there.
 */
export function drinkOccasions(state: PageState): number {
  if (state.occasion === CONFERENCE) return conferenceElements(conferenceSelectionFrom(state.conference)).total
  if (state.occasion === CONFERENCE_BREAK_OCCASION) return 1
  return 0
}

/**
 * **Run 19 §16, *Which servings are morning* (DECIDED for now, Barna
 * 20261018).** How many servings of each kind the day holds.
 *
 *   * the conference's **welcome is a morning serving**, because arrival is
 *     the peak;
 *   * **with lunch**, the breaks are placed around it: 1 break is before
 *     lunch, 2 are one each side, 3 are two before and one after;
 *   * **without lunch**, the breaks take the rate of the conference's own
 *     start time — before 12:00 morning, from 12:00 afternoon. The welcome
 *     keeps its own rule, which has its own reason; before noon the two
 *     readings agree anyway;
 *   * a standalone *Kávészünet* is one serving, read the same way;
 *   * **lunch itself is not a coffee serving.** §16's own check figure says
 *     so: 40 guests with a welcome, two breaks and lunch come to 118 cups,
 *     which is 40 × (2 × 1,15 + 1 × 0,65) and counts three servings, not
 *     four.
 *
 * No further weighting between breaks of the same half day. A break still
 * has no time of its own — **assumption A21 `coffeeServings`** — and
 * inventing a timetable for a conference would be inventing operational
 * data.
 *
 * This replaces `coffeeOccasions`, which counted the **lunch** element as a
 * coffee serving and read every break at the event's own start time.
 */
export function coffeeServings(state: PageState): CoffeeServings {
  const beforeNoon = String(state.start ?? '').slice(0, 5) < COFFEE_AFTERNOON_FROM
  if (state.occasion === CONFERENCE_BREAK_OCCASION) {
    return beforeNoon ? { morning: 1, afternoon: 0 } : { morning: 0, afternoon: 1 }
  }
  if (state.occasion !== CONFERENCE) return { morning: 0, afternoon: 0 }
  const chosen = conferenceSelectionFrom(state.conference)
  const welcome = chosen.welcome ? 1 : 0
  if (!chosen.lunch) {
    return beforeNoon
      ? { morning: welcome + chosen.breaks, afternoon: 0 }
      : { morning: welcome, afternoon: chosen.breaks }
  }
  const before = chosen.breaks <= 1 ? chosen.breaks : chosen.breaks === 2 ? 1 : 2
  return { morning: welcome + before, afternoon: chosen.breaks - before }
}

/**
 * **Run 19 §16: `coffee_dish_id` is retired.**
 *
 * The old design priced a cup from a catalogue row, and the row was never
 * named — which is what kept F7's question hidden from run 7 to run 18. A
 * cup of coffee is not a dish that is made and packed: it is a machine
 * standing on a table for a day plus the beans and the milk that go through
 * it, and those are register figures, not catalogue rows.
 *
 * The key keeps its row and its place on the browser allow-list, marked
 * RETIRED — nothing in the register is ever deleted — and the constant
 * stays because `refreshmentDrinkDish` still refuses to be given it: F3's
 * refreshment drink and F7's coffee were always two questions and must not
 * collapse into one, however coffee is priced.
 */
export const COFFEE_DISH_PARAM = 'coffee_dish_id'

/**
 * §12: which catalogue row is the conference refreshment drink. **Not in the
 * register today** — it is a fourth value marked *needed*, and Barna or
 * Balázs names the row.
 */
export const REFRESHMENT_DISH_PARAM = 'refreshment_dish_id'

/**
 * F3's refreshment drink row — **the row §12 names, and no other**.
 *
 * F3 says *the refreshment drink rows* and the catalogue has no field that
 * says which drink refreshes a conference, only `is_drink`. This stage first
 * took the first published non-coffee drink carrying a unit volume, and its
 * spec audit was right that catalogue order is not a decision: the day a
 * volume appeared on any drink row, that row would have become a priced,
 * customer-visible line with nobody having chosen it. So the choice is a
 * parameter, it is **empty**, and an empty parameter keeps the line hidden
 * exactly as an empty volume does.
 *
 * *Coffee is F7*, so the coffee row is refused here even if `§12` names it
 * for both: the two lines are different questions and must not collapse into
 * one. Run 7 kept them apart with a name match on *americano*; item 90
 * replaces that with the one thing that is a decision — the row
 * `coffee_dish_id` names. Both parameters are empty today, and two empty
 * parameters are not equal to each other, so nothing is refused by accident.
 */
export function refreshmentDrinkDish(
  drinks: readonly MenuDish[],
  dishId: string | null | undefined,
  coffeeDishId: string | null | undefined,
): MenuDish | null {
  const wanted = String(dishId ?? '').trim()
  if (wanted === '') return null
  if (wanted === String(coffeeDishId ?? '').trim()) return null
  return (
    drinks.find(
      (dish) =>
        dish.id === wanted &&
        dish.raw.net_price !== null &&
        dish.unitVolumeLitres !== null,
    ) ?? null
  )
}

/** One worked-out drink line, ready for the engine and for the offer. */
export interface ComputedDrink {
  dish: MenuDish
  occasions: number
  /** The whole catalogue units made. */
  units: number
  /** What is charged for: whole units' worth. */
  portions: number
}

/**
 * F3's refreshment drink for the conference elements. Null — the line hidden
 * — when the occasion is not a conference, when §12 names no refreshment
 * drink row, when the row it names carries no unit volume, or when §12 has
 * no litres-per-head figure. **Two of those four are empty today.**
 */
export function refreshmentDrinkOffer(args: {
  state: PageState
  context: MenuContext
  drinks: readonly MenuDish[]
  guests: number
}): ComputedDrink | null {
  const { state, context, drinks, guests } = args
  if (state.occasion !== CONFERENCE) return null
  const dish = refreshmentDrinkDish(
    drinks,
    context.params[REFRESHMENT_DISH_PARAM],
    context.params[COFFEE_DISH_PARAM],
  )
  if (dish === null || dish.unitVolumeLitres === null) return null
  const perHead = context.params[REFRESHMENT_LITRES_PARAM]
  if (perHead === null || perHead === undefined || String(perHead).trim() === '') return null
  const occasions = drinkOccasions(state)
  const quantity = refreshmentDrinkQuantity({
    guests,
    occasions,
    litresPerHeadPerOccasion: fromDecimal(String(perHead)),
    unitVolumeLitres: fromDecimal(String(dish.unitVolumeLitres)),
  })
  if (quantity === null) return null
  return { dish, occasions, units: quantity.units, portions: quantity.units }
}

/**
 * **Run 19 §16: F7's coffee offer**, and with it whether the question may
 * be asked at all.
 *
 * **Null means the question stays hidden**: the occasion is not one of the
 * two, the customer did not ask for on-site staff, or the register is
 * missing any one of §16's eleven figures. One check hides the question,
 * both offer lines and the stored machine record together, which is what
 * item 90 (P11) asks of this function and what `coffeeQuestionShown` reads.
 *
 * It no longer takes `drinks`: there is no catalogue row behind a cup any
 * more. The arithmetic is `coffeeOfferOf` in `@plt/engine`, which the
 * server runs on the same reading (§19.2 principle 2).
 */
export function coffeeOffer(args: {
  state: PageState
  context: MenuContext
  guests: number
}): CoffeeOffer | null {
  const { state, context, guests } = args
  if (!COFFEE_OCCASIONS.includes(state.occasion)) return null
  /* Run 16 item 8(c) and run 19 §16: *offered only with on-site staff*, and
     item 8(d) names the case — **no staff, no coffee**. Switching staff back
     to *Nem* resets the coffee answer, which `CustomerPage` does; this
     function only answers whether there is an offer to make. F1's own
     question and its own confirmation reason are untouched: coffee still
     raises `coffee` and staff still raises `staff_equipment`, and neither
     answer sets the other. */
  if (state.staff !== true) return null
  const coffeeParams = coffeeParamsFrom(context.params)
  if (coffeeParams === null) return null
  return coffeeOfferOf({ guests, servings: coffeeServings(state) }, coffeeParams)
}

/** F7: the question is on the page only when there is an offer to make. */
export const coffeeQuestionShown = (offer: CoffeeOffer | null): boolean => offer !== null

/** The two service keys §16 names, spelled once so nothing else spells them. */
export const COFFEE_LINE = 'coffee'
export const COFFEE_MACHINE_LINE = 'coffee_machine'

/**
 * §16's two offer lines, as the money model takes them: the machine, which
 * is a fixed cost per day, and the cups, which vary with the guests.
 *
 * Empty where there is no coffee to price **or the customer has not asked
 * for it**: the question is one thing and the answer is another, and an
 * unanswered question charges nothing.
 */
export function coffeeServiceLines(args: {
  offer: CoffeeOffer | null
  asked: boolean
  params: EngineParams
}): ServiceLine[] {
  const { offer, asked, params } = args
  if (offer === null || !asked) return []
  return [
    serviceLine(
      COFFEE_MACHINE_LINE,
      offer.machines,
      /* The **per machine** charge: `serviceLine` multiplies by the
         quantity, so handing it the line total would charge the machines
         once for each of them. */
      offer.machineUnitChargeNet,
      params.money.vatPct,
    ),
    serviceLine(COFFEE_LINE, offer.cups, offer.cupChargeNet, params.money.vatPct),
  ]
}

export interface Limits {
  sav: DishCountSuggestion
  sw: DishCountSuggestion
}

export const limitsOf = (occasion: Occasion, guests: number, params: EngineParams): Limits => ({
  sav: dishCountSuggestion(occasion, guests, 'sav', params.quantity),
  sw: dishCountSuggestion(occasion, guests, 'sw', params.quantity),
})

/** §5.3 pre-selection order, for one side of the menu. */
export function rankedDishes(
  dishes: readonly MenuDish[],
  occasion: Occasion,
  side: Side,
): MenuDish[] {
  const onSide = dishes.filter((dish) => dish.side === side)
  const byId = new Map(onSide.map((dish) => [dish.id, dish]))
  const engineDishes = onSide.map((dish) => dish.engine as Dish)
  return rankForPreselection(engineDishes, occasion, side)
    .map((dish) => byId.get(dish.id))
    .filter((dish): dish is MenuDish => dish !== undefined)
}

export interface ResolvedSelection {
  selection: Selection
  /** The occasion and lane this selection belongs to (DR-21). */
  key: string
  /** The §6.6 limits in force, as a key, so the note cannot outlive them. */
  limitKey: string
  /** True where the selection was built fresh rather than carried over. */
  preselected: boolean
  /** DR-21: a page limit took dishes off a side. */
  clipped: boolean
  /** Run 15 item 1: a quantity belongs to a dish that is on the menu. */
  quantities: Record<string, number>
}

/**
 * vB6's `ensureSel`, unchanged in substance.
 *
 * DR-21 (`build/FIXES.md` item 24), option C: the headcount band is **not**
 * part of the key, so typing 60 where 40 stood keeps the selection — a
 * different occasion earns a different recommendation, a different headcount
 * does not. §6.6's page limits still bind, and when one drops the dishes that
 * go are the ones ranked lowest, not the ones chosen last.
 */
export function resolveSelection(args: {
  state: PageState
  available: readonly MenuDish[]
  occasion: Occasion
  limits: Limits
  lane: Lane
  /**
   * Run 19 §7: the guard that stops the top of the range from inflating a
   * small order needs the figures the recommendation is worked out from.
   * Optional, so the one call that has no parameters to hand — none today —
   * still opens on the top of the range, which is the item's own rule.
   */
  params?: EngineParams
}): ResolvedSelection {
  const { state, available, occasion, limits, lane, params } = args
  const key = [state.occasion, lane].join('|')
  const limitKey = `${limits.sav.limit}|${limits.sw.limit}`
  const fresh = state.selection === null || state.selectionKey !== key

  /* Run 19 §7 (DECIDED 20261016, Barna): the menu opens on the **top** of
     the occasion's dish range, with `preselectCount`'s guard dropping
     dishes from the end of the preference order while a pre-selected dish's
     share would be under one production unit. `limits.*.preselect` is the
     top of the range and is what the guard starts from. */
  const opening = (side: Side): string[] => {
    const ranked = rankedDishes(available, occasion, side)
    const count =
      params === undefined
        ? (side === 'sav' ? limits.sav : limits.sw).preselect
        : preselectCount(
            ranked.map((dish) => dish.engine as Dish),
            occasion,
            { servingWindow: state.servingWindow, profile: state.profile, balance: state.balance as Balance },
            guestsOf(state),
            side,
            params.quantity,
          )
    return ranked.slice(0, count).map((dish) => dish.id)
  }

  let selection: Selection = fresh
    ? { sav: opening('sav'), sw: opening('sw') }
    : { sav: [...(state.selection?.sav ?? [])], sw: [...(state.selection?.sw ?? [])] }

  const published = new Set(available.map((dish) => dish.id))
  let clipped = fresh ? false : state.clipped && state.clipLimitKey === limitKey

  const clip = (side: Side, max: number): void => {
    const key_: 'sav' | 'sw' = side
    const ids = selection[key_].filter((id) => published.has(id))
    if (ids.length <= max) {
      selection[key_] = ids
      return
    }
    const rank = new Map(rankedDishes(available, occasion, side).map((dish, i) => [dish.id, i]))
    const keep = new Set(
      [...ids]
        .sort((a, b) => (rank.get(a) ?? 1e9) - (rank.get(b) ?? 1e9))
        .slice(0, max),
    )
    selection[key_] = ids.filter((id) => keep.has(id))
    clipped = true
  }
  clip('sav', limits.sav.limit)
  clip('sw', limits.sw.limit)

  /* Run 15 item 1(f): a dish that leaves the menu takes its quantity with it,
     exactly as the emphasis it replaces did, so a dish re-added any other way
     comes back on its recommendation. The undo note keeps its own copy in
     `state.removed`, which is what lets that one route put it back as it was. */
  const quantities = fresh
    ? {}
    : Object.fromEntries(
        Object.entries(state.quantities).filter(
          ([id]) => selection.sav.includes(id) || selection.sw.includes(id),
        ),
      )

  return { selection, key, limitKey, preselected: fresh, clipped, quantities }
}

/* ------------------------------------------------------------------ *
 * The order
 * ------------------------------------------------------------------ */

export interface PricedPage {
  guests: number
  glutenFreeGuests: number
  priced: PricedOrder
  /**
   * The dishes Planteen **added** for the gluten-free guests (§6.7 step 6):
   * dishes that are not on the customer's own menu and are ticked for them.
   * Since run 17 item B5(b) a gluten-free dish the customer already chose
   * carries their share on its own line and is **not** here — it was not
   * added by anybody; it was already there.
   */
  glutenFreeAuto: Set<string>
  /**
   * Run 17 item B5(b): what each dish holds for the gluten-free guests, by
   * dish ID — the figure *ebből {n} db gluténmentes vendégeknek* prints and
   * the kitchen's own labelled box holds. Covers both: a share carried
   * inside a chosen dish's quantity and a whole separate gluten-free line.
   */
  glutenFreeShares: Map<string, number>
  /** §6.7 step 6: a side has gluten-free guests and no eligible dish. */
  glutenFreeUnavailable: boolean
  /** Fix list item 2: which sides have gluten-free guests and no eligible
   *  dish. Informational only; nothing blocks on it. */
  glutenFreeMissing: { sav: boolean; sw: boolean }
  /** §6.6: a chosen line falls below its catalogue minimum production. */
  belowMinimumProduction: boolean
  /** Pieces made across both sides, which §8.3's ceiling is measured in. */
  pieces: number
  /**
   * Run 15 item 1: what is made of each dish on the menu, by dish ID — the
   * figure the − / + steps, the recommendation *Vissza az ajánlotthoz* goes
   * back to, and whether the customer set it. One entry per dish with a line,
   * so the card, the mark, the restore link and the offer line all read the
   * same number from one place (§9 of the constraints).
   */
  quantities: Map<string, DishQuantity>
  byId: Map<string, MenuDish>
}

/** Run 15 item 1: one dish's quantity, as the menu card needs it. */
export interface DishQuantity {
  /** The dish's total on its side, main and gluten-free box together. */
  ordered: number
  /** The same with no customer quantity on it, at today's answers. */
  recommended: number
  /** True where the customer set it with − / + (item 1(d)'s mark). */
  customerSet: boolean
  /** §6.7 step 5: what one press moves it by. */
  unit: number
}

/**
 * Run 9 item 5 and run 14 item 3 both measure a dish once, main and
 * gluten-free box together, so this does too: a dish with a box line has its
 * two lines added up, and the − / + steps that total.
 */
export function dishQuantitiesOf(
  priced: PricedOrder,
  byId: ReadonlyMap<string, MenuDish>,
): Map<string, DishQuantity> {
  const out = new Map<string, DishQuantity>()
  for (const line of priced.lines) {
    if (line.isDrink || line.priceCategory === 'gyumolcs') continue
    const unit = byId.get(line.dishId)?.engine?.productionUnit ?? 1
    const held = out.get(line.dishId)
    out.set(line.dishId, {
      ordered: (held?.ordered ?? 0) + line.orderedPortions,
      recommended: (held?.recommended ?? 0) + line.recommendedPortions,
      customerSet: (held?.customerSet ?? false) || line.qtySource === 'customer',
      unit: Math.max(Math.round(unit), 1),
    })
  }
  return out
}

export function priceThePage(args: {
  state: PageState
  /** The whole reading: a drink is outside `available` and is looked up here. */
  context: MenuContext
  available: readonly MenuDish[]
  fruitAvailable: readonly MenuDish[]
  occasion: Occasion
  params: EngineParams
  selection: Selection
  /** Run 15 item 1: dish ID -> the quantity the customer set with the stepper. */
  quantities: Readonly<Record<string, number>>
  /**
   * `build/FIXES.md` items 74 and 78: drink lines the build worked out rather
   * than the customer — F3's conference refreshment drink, F7's coffee. Each
   * carries its own rounded quantity, so the engine takes it as it is. Empty
   * today, because both are waiting on a *needed* value.
   */
  computedDrinks?: readonly ComputedDrink[]
}): PricedPage {
  const { state, available, fruitAvailable, occasion, params, selection, quantities } = args
  const byId = new Map(available.map((dish) => [dish.id, dish]))
  const guests = guestsOf(state)
  /* §6.7 step 6: gluten-free guests are part of the guest count, never added
     to it. The field keeps what the customer typed; every reader clamps. */
  const glutenFreeGuests = Math.min(Math.max(state.glutenFreeGuests, 0), guests)

  const chosen = (side: Side): Dish[] =>
    selection[side]
      .map((id) => byId.get(id)?.engine)
      .filter((dish): dish is Dish => dish !== undefined && dish !== null)

  const eligible = available
    .filter((dish) => dish.glutenFreeEligible)
    .map((dish) => dish.engine)
    .filter((dish): dish is Dish => dish !== null)

  const fruitById = new Map(fruitAvailable.map((dish) => [dish.id, dish]))
  const fruit = state.fruit
    .map((id) => fruitById.get(id))
    .filter((dish): dish is MenuDish => dish !== undefined)
    .map((dish) => ({
      id: dish.id,
      productionUnit: dish.raw.production_unit ?? 1,
      netPrice: dish.raw.net_price ?? 0,
    }))

  /* Run 15 item 2(a): a drink the customer ticked is a whole quantity, not a
     count per guest. The stepper counts bottles in the drink's own production
     units, so the figure reaches the engine already rounded and nothing
     multiplies it by the headcount. */
  const ticked = Object.entries(state.drinks)
    .filter(([, value]) => value.on)
    .map(([id, value]) => {
      const dish = byIdAll(args.context, id)
      if (dish === null) return null
      const unit = Math.max(Math.round(dish.production_unit ?? 1), 1)
      const portions = Math.max(Math.ceil(Math.max(value.bottles, 0) / unit) * unit, unit)
      return {
        id,
        netPrice: dish.net_price ?? 0,
        portions,
        productionUnits: portions / unit,
        customerSet: true,
      }
    })
    .filter((drink): drink is NonNullable<typeof drink> => drink !== null)

  /* One dish, one line — the Összesítés must not show the same drink twice —
     and `build/FIXES.md` item 78 says which of the two wins: *The customer
     can change the quantity like any other line.* So **the customer's own
     tick wins** and the computed quantity is only what the line opens on.
     Ticking the dish in the Italok block and setting a per-guest count is
     how it is changed, which is §5.6 working exactly as it does for every
     other drink; untouched, the build's figure stands. */
  /* Run 17 item B12(c): F3's refreshment line *shows as a normal drinks line
     the customer can change*. The moment the customer touches that row —
     typing a figure, stepping it, pressing *Vendégenként 1*, or unticking it
     — `state.drinks` holds an answer for the dish, and theirs is the one that
     counts. Unticked means unticked: the line does not come back. Until then
     the build's own figure stands. */
  const touchedIds = new Set(Object.keys(state.drinks))
  const computed = (args.computedDrinks ?? [])
    .filter((entry) => !touchedIds.has(entry.dish.id))
    .map((entry) => ({
      id: entry.dish.id,
      netPrice: entry.dish.raw.net_price ?? 0,
      portions: entry.portions,
      productionUnits: entry.units,
      /* Run 15 item 2(c): F3's refreshment drink and F7's coffee are the
         system's own lines and stay `recommendation`, per guest. */
      customerSet: false,
    }))
  const drinks: DrinkSelection[] = [...ticked, ...computed]

  const input: OrderInput = {
    occasion,
    guests,
    glutenFreeGuests,
    servingWindow: state.servingWindow,
    profile: state.profile,
    balance: state.balance as Balance,
    savouryDishes: chosen('sav'),
    sweetDishes: chosen('sw'),
    quantities,
    glutenFreeEligible: eligible,
    fruit,
    drinks,
  }

  const priced = priceOrder(input, params)
  const chosenIds = new Set([...selection.sav, ...selection.sw])
  const glutenFreeAuto = new Set(
    [...priced.sides.sav.glutenFreeLines, ...priced.sides.sw.glutenFreeLines]
      .map((line) => line.dish.id)
      .filter((id) => !chosenIds.has(id)),
  )
  /* Run 17 item B5(b): the share, wherever it sits. */
  const glutenFreeShares = new Map<string, number>()
  for (const side of ['sav', 'sw'] as const) {
    for (const line of priced.sides[side].all) {
      const share = line.glutenFreePortions ?? 0
      if (share > 0) glutenFreeShares.set(line.dish.id, (glutenFreeShares.get(line.dish.id) ?? 0) + share)
    }
  }
  return {
    guests,
    glutenFreeGuests,
    priced,
    glutenFreeAuto,
    glutenFreeShares,
    glutenFreeUnavailable:
      priced.sides.sav.glutenFreeUnavailable || priced.sides.sw.glutenFreeUnavailable,
    glutenFreeMissing: {
      sav: priced.sides.sav.glutenFreeUnavailable && toNumber(priced.recommended.savoury) > 0,
      sw: priced.sides.sw.glutenFreeUnavailable && toNumber(priced.recommended.sweet) > 0,
    },
    belowMinimumProduction: priced.problems.some(
      (problem) => problem.code === 'below_minimum_production',
    ),
    pieces: priced.sides.sav.portions + priced.sides.sw.portions,
    quantities: dishQuantitiesOf(priced, byId),
    byId,
  }
}

const byIdAll = (context: MenuContext, id: string): PublishedDish | null =>
  context.dishes.find((dish) => dish.id === id) ?? null

/* ------------------------------------------------------------------ *
 * The money (§5.11)
 * ------------------------------------------------------------------ */

export interface PageTotals {
  total: OrderTotal
  view: SummaryView
  zone: string
  zoneRow: DeliveryZone | undefined
  pickup: boolean
  blocked: boolean
}

export function totalsOf(args: {
  state: PageState
  context: MenuContext
  params: EngineParams
  priced: PricedOrder
  slots: SlotRow[]
  guests: number
  /**
   * Run 19 §16: the priced service lines — the coffee and the machine.
   * Omitted, there are none, which is every order before this run and every
   * occasion that does not offer coffee.
   */
  services?: readonly ServiceLine[]
}): PageTotals {
  const { state, context, params, priced, slots, guests } = args
  const pickup = state.fulfilment === 'pickup'
  const zone = context.zone
  const delivery = deliveryLine(
    {
      zone,
      pickup,
      deliveryBlocked: deliveryBlocked(state, slots),
      foodGross: priced.food.gross,
    },
    params.money,
  )
  const total = orderTotal(priced.food, delivery, args.services ?? [])
  return {
    total,
    view: summaryView(
      total,
      { buyer: state.business ? 'business' : 'consumer', guests },
      params.money,
    ),
    zone,
    zoneRow: params.money.zones[zone],
    pickup,
    blocked: delivery.blocked,
  }
}

/* ------------------------------------------------------------------ *
 * Run 17 items B9 and B11: one Állófogadás, and the meal question
 * ------------------------------------------------------------------ */

/** §6.1 `fog_est`: the evening reception, which replaces dinner. */
export const RECEPTION_EVENING = 'fog_est'
/** §6.1 `fog_nap`: the daytime reception, which is not a meal. */
export const RECEPTION_DAYTIME = 'fog_nap'
/** The value of the one chip the two are shown under (item B9(a)). */
export const RECEPTION_CHIP = 'fogadas'

/**
 * **Run 17 item B9(a) (DECIDED 20261015, Barna).** *Nappali állófogadás* and
 * *Esti állófogadás* are **one occasion on the page**, *Állófogadás* /
 * *Standing reception*. Both figure sets are kept exactly as they are — the
 * two `public.occasions` rows are untouched — and item B11 decides which of
 * them an order uses, from one question.
 *
 * `state.occasion` therefore still holds a **real** occasion key, `fog_est`
 * or `fog_nap`, and that is what is priced, sent and stored. Nothing
 * downstream learns a new word, and an order placed before today keeps its
 * stored occasion and reads exactly as it did.
 *
 * There is **no separate *Networking* occasion** on this page: networking is
 * part of the `fog_nap` row's own name (*Nappali állófogadás, networking*)
 * and so uses the daytime figure set. The run 17 report says so.
 */
export const isReception = (key: string): boolean =>
  key === RECEPTION_EVENING || key === RECEPTION_DAYTIME

/** B11: the order replaces a meal exactly when it is on the evening set. */
export const replacesMeal = (key: string): boolean => key === RECEPTION_EVENING

/** B11: which figure set the answer chooses. */
export const occasionForMeal = (replaces: boolean): string =>
  replaces ? RECEPTION_EVENING : RECEPTION_DAYTIME

/** §12 `meal_default_from`: the start time from which *Igen* is pre-selected. */
export const MEAL_DEFAULT_FROM_PARAM = 'meal_default_from'

/** `HH:MM` as minutes past midnight, or null where it is not a time. */
const clockMinutes = (text: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(text ?? '').trim())
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * **Run 17 item B11(a).** *Igen* from `meal_default_from` (18:00), *Nem*
 * before it. The cut-off is a register value and never a literal here; an
 * unreadable or missing one leaves the answer at *Nem*, which is the
 * lighter of the two and the one that cannot over-cater by default.
 */
export function mealReplacementDefault(start: string, from: string): boolean {
  const at = clockMinutes(start)
  const cut = clockMinutes(from)
  if (at === null || cut === null) return false
  return at >= cut
}

/* ------------------------------------------------------------------ *
 * Run 17 item B7: the minimum order, said early
 * ------------------------------------------------------------------ */

/**
 * **Run 17 item B7 (DECIDED 20261015, Barna).** The customer is told the
 * minimum order value on the offer card and on the summary, as soon as the
 * food total is below it — rather than finding out at the delivery step.
 *
 * **Which minimum.** `min_pickup` on collection; the zone's own minimum once
 * a delivery address is known. Before either is settled — a delivery with no
 * zone this reading can resolve — `min_pickup` stands, which is the smaller
 * of the two and the one the page already names in step 01.
 *
 * **Which basis.** Gross for a consumer, net for a business buyer, as every
 * other amount those two buyers see (§5.11).
 *
 * **What is counted — run 19 §15 (DECIDED 20261018, Barna).** The
 * comparison was on the **gross food value**; it is now on **every line of
 * the order, gross**: food, drinks, coffee, staff and equipment. Counting
 * food only would leave customers working out which lines qualify. The
 * **delivery fee is still not counted** (PROPOSED), because counting it
 * would lower each zone's threshold by that zone's own fee.
 *
 * `minimumBasisGross` in `@plt/engine` is that figure, and `rulesOf` blocks
 * on the same function — so the line appears exactly while the block would
 * fire, and the two cannot disagree.
 */
export interface MinimumOrder {
  /** True while the order total, less delivery, is under the minimum. */
  below: boolean
  /** The minimum, in the basis this buyer reads. */
  minimumShown: number
  /** What is still missing, in the same basis. Zero when nothing is. */
  missingShown: number
  /** Which minimum applies: the collection one or a delivery zone's. */
  basis: 'pickup' | 'zone'
}

export function minimumOrderOf(args: {
  totals: PageTotals
  business: boolean
  params: EngineParams
}): MinimumOrder {
  const { totals, business, params } = args
  const zoneMinimum = totals.pickup ? 0 : (totals.zoneRow?.minOrderGross ?? 0)
  const usesZone = !totals.pickup && zoneMinimum > 0
  const minimumGross = usesZone ? zoneMinimum : params.money.minPickupGross
  /* Run 19 §15: the whole order, gross, less the delivery fee. */
  const basisGross = minimumBasisGross(totals.total)
  const missingGross = Math.max(minimumGross - basisGross, 0)
  const show = (gross: number): number =>
    business ? netOfGross(gross, params.money.vatPct) : gross
  return {
    below: basisGross < minimumGross,
    minimumShown: show(minimumGross),
    missingShown: show(missingGross),
    basis: usesZone ? 'zone' : 'pickup',
  }
}

/* ------------------------------------------------------------------ *
 * The rules (§5.9, §8.3, §11.3, §11.4)
 * ------------------------------------------------------------------ */

/**
 * Barna's ruling 11(b) of 8 October 2026: the confirmation reasons get their
 * own closed type, `public.confirmation_reason`, with exactly these seven
 * values. **`083_confirm_first` owns the type, the new status
 * `awaiting_confirmation` and the routing**, because routing is F8's. The
 * names are spelled here so that what this stage raises and what `083`
 * stores are the same seven strings and no rename is needed between them.
 */
export type ConfirmationReasonCode =
  | 'staff_equipment'
  | 'coffee'
  | 'dietary_note'
  | 'custom_menu'
  | 'other_food'
  | 'contract_value'
  | 'customer_comment'
  /* `build/FIXES.md` item 81 (P2), Barna's ruling 2 of 8 October 2026: the six
     automatic reasons of §11.4 joined `public.confirmation_reason`, under the
     names `public.review_reason` spells them. The page raises five of them --
     `new_customer_value` is the server's alone, because only the server knows
     whether this customer has ordered before. */
  | 'value'
  | 'covers'
  | 'clash_date'
  | 'zone_c'
  | 'capacity_soft'
  | 'new_customer_value'
  /* Run 9, item 4: over portion_cap or max_covers_per_day. Silent. */
  | 'capacity_over'

/** One reason, by its ruled code and the sentence the customer reads. */
export interface ConfirmationReason {
  code: ConfirmationReasonCode
  text: string
}

export interface RuleSet {
  /** §11.3: the order cannot be placed until these are answered. */
  block: string[]
  /**
   * §11.4: it can, and a colleague approves it afterwards. **Always empty
   * since `build/FIXES.md` item 81 (P2)**: the five automatic reasons the page
   * measures are confirmation reasons now and are in `confirmation` below, and
   * no new order carries a review reason at all. The field stays -- nothing in
   * run 8 drops one -- and so does the pay-first note in `Step4Summary` that
   * reads it, for the orders placed before run 8 that are still in that lane.
   */
  review: string[]
  /** §5.9: it becomes an enquiry and a colleague calls. */
  enquiry: string[]
  /**
   * `build/FIXES.md` item 79 (F8) and ruling 11(b): the reasons a person has
   * to look at the order before it is priced for certain.
   *
   * **Non-empty is the whole test.** The payment choice is hidden, the order
   * button becomes *Elküldöm visszaigazolásra*, the confirmation note is
   * shown, and the server routes the order to `awaiting_confirmation` — and
   * nothing about any of that reads `review`, which is the pay-first lane of
   * §11.4 and is untouched.
   */
  confirmation: ConfirmationReason[]
  short: boolean
}

type T = (hu: string, en: string) => string

export function rulesOf(args: {
  state: PageState
  context: MenuContext
  calendar: Calendar
  page: PricedPage
  totals: PageTotals
  slots: SlotRow[]
  t: T
  money: (value: number) => string
  /**
   * `build/FIXES.md` item 78 (F7): the customer asked for coffee **and** the
   * question was there to ask, which it is only once both *needed* values
   * exist. Passed in rather than read off the state, so a stale `true` left
   * behind by a withdrawn catalogue row cannot raise a reason for a line the
   * page is not showing.
   */
  coffeeAsked?: boolean
}): RuleSet {
  const { state, context, calendar, page, totals, slots, t, money } = args
  const params = context.params
  const number = (key: string): number => Number(params[key] ?? 0)

  const block: string[] = []
  const review: string[] = []
  const enquiry: string[] = []
  const confirmation: ConfirmationReason[] = []

  /**
   * One reason, raised once, by its ruled code and with the sentence the
   * customer reads beside it.
   *
   * Until `083_confirm_first` it also pushed the sentence on to `enquiry`,
   * because *send for confirmation* had nowhere else to go. It no longer
   * does: ruling 9 gives the confirmation its own status, its own button and
   * its own note, and `enquiry` is now the §8.3 short-notice lane alone —
   * which is the one route item 79 leaves exactly as it was.
   */
  const confirm = (code: ConfirmationReasonCode, hu: string, en: string): void => {
    confirmation.push({ code, text: t(hu, en) })
  }

  /**
   * `build/FIXES.md` item 81 (P2): the six automatic reasons of §11.4 route
   * exactly as the other seven do and **say nothing to the customer**. "These
   * six reasons are not shown to the customer, as the review reasons are not
   * shown today. The customer sees only the run 7 confirmation note and
   * button." §5.11 has kept them off the page since the first draft; item 81
   * keeps them off it under their new name.
   *
   * So the text is empty, and it is empty on purpose: run 8's brief writes no
   * new customer-facing wording and forbids inventing a sentence for a reason.
   * Nothing renders `ConfirmationReason.text` today — the note above the
   * button is one fixed paragraph — and if something one day wants to, these
   * six are the ones it must leave out.
   */
  const confirmSilently = (code: ConfirmationReasonCode): void => {
    confirmation.push({ code, text: '' })
  }

  /** §11.4's six, as codes, so the five the page measures can be raised as
   *  confirmation reasons off the short-notice lane and as §8.3's one enquiry
   *  reason on it. */
  const automatic: ConfirmationReasonCode[] = []

  /* §5.9: an enquiry reason replaces the order button.
     `build/FIXES.md` item 72 (F1), Barna's ruling 1 of 8 October 2026: staff
     and equipment are one package — the Planteen provides no equipment
     without staff — so the three sub-options are gone and the reason says
     what the one question now asks. */
  if (state.staff) {
    confirm(
      'staff_equipment',
      'helyszíni kiszolgálást kér személyzettel és eszközökkel',
      'you asked for on-site service with staff and equipment',
    )
  }
  /* `build/FIXES.md` item 78 (F7), ruling 3: coffee needs serving equipment
     and equipment comes only with staff, so it adds its own reason and does
     **not** change the F1 answer. */
  if (args.coffeeAsked === true) {
    confirm(
      'coffee',
      'kávét kér, személyzettel és eszközökkel',
      'you asked for coffee, with staff and equipment',
    )
  }
  if (state.dietaryNote.trim() !== '') {
    confirm('dietary_note', 'étrendi megjegyzés a konyhának', 'a dietary note for the kitchen')
  }
  if (state.custom) confirm('custom_menu', 'egyedi menüt kér', 'you asked for a custom menu')
  /* `build/FIXES.md` item 79 (F8): "A non-empty comment adds confirmation
     reason `customer_comment`." */
  if (state.comment.trim() !== '') {
    confirm(
      'customer_comment',
      'megjegyzést vagy különleges kérést írt',
      'you wrote a comment or a special request',
    )
  }
  /* Run 9, item 2 (DECIDED 20261010): the question whether all the food
     comes from the Planteen is gone -- "assume we feed everyone" -- and so is
     `other_food`. The code stays in the type for the orders that carry it. */
  /* §5.9's contract threshold is measured on the **food** gross, as vB6
     measures it and as `build/FIXES.md` item 13 — Barna's ruling on DR-10,
     option A — requires. The minimum order value above is the same figure;
     only §11.4's `review_value` reads the order gross.
     This stage first changed it to the order gross, to agree with the server.
     The spec audit caught it: the server was the one out of step, and a
     reading an agent took in 025 cannot outrank an item in FIXES.md. Both
     are the food gross now. */
  if (totals.total.food.gross > number('contract_value')) {
    confirm(
      'contract_value',
      `az érték ${money(number('contract_value'))} felett van`,
      `the value is above ${money(number('contract_value'))}`,
    )
  }

  /* blocking */
  if (!calendar.ok) {
    block.push(t('Nincs megadva dátum.', 'No date given.'))
  } else {
    if (calendar.lane === 'closed') {
      block.push(
        t(
          `az eseményig kevesebb mint ${number('short_notice_bd')} munkanap van`,
          `the event is fewer than ${number('short_notice_bd')} business days away`,
        ),
      )
    }
    if (calendar.holiday) {
      block.push(
        t('az esemény napja munkaszüneti nap', 'the event date is a public holiday'),
      )
    }
    if (deliveryBlocked(state, slots)) {
      block.push(
        t(
          'erre a kezdési időpontra nincs kiszállítási sávunk',
          'there is no delivery slot for this start time',
        ),
      )
    }
    /* DR-2 (build/FIXES.md item 5): the Hungarian no longer names the
       capacity; the English, which already read as plain availability, is
       unchanged. The page is told the date is full, never how full. */
    if (context.capacity.day_full) {
      block.push(
        t('Erre a napra már nem tudunk rendelést felvenni.', 'this date is fully booked'),
      )
    }
  }

  /* §3.7: a delivery beyond the radius cannot be ordered. The block is the
     same sentence the fulfilment card shows, so the page says one thing. */
  if (state.fulfilment === 'delivery' && outsideDeliveryRadius(context)) {
    block.push(outsideRadiusSentence(t, context))
  }

  const recommended = page.priced.recommended
  if (state.selection !== null) {
    /* `.all`, not `.lines`: run 9 item 5 makes the chosen dishes for the
       guests who are not gluten-free, so when every guest is, the side's
       food is all in the box and the basket is not empty. */
    if (page.priced.sides.sav.all.length === 0 && toNumber(recommended.savoury) > 0) {
      block.push(t('válasszon legalább egy sós fogást', 'choose at least one savoury dish'))
    }
    if (page.priced.sides.sw.all.length === 0 && toNumber(recommended.sweet) > 0) {
      block.push(t('válasszon legalább egy édes fogást', 'choose at least one sweet dish'))
    }
  }

  /* DR-2: say what to change, not the production reason. The same sentence
     stands in the menu warning, so the two agree in both languages. */
  if (page.belowMinimumProduction) {
    block.push(
      t(
        'Ennyi vendéghez ez túl sokféle fogás. Kérjük, válasszon kevesebbet.',
        'That is too many different dishes for this many guests. Please choose fewer.',
      ),
    )
  }

  const minimum = totals.total.delivery.minimumGross
  /* The amount is in brackets rather than in front of the noun: an amount used
     as a modifier takes the adjectival -os in Hungarian, and the article in
     front of an interpolated number is the defect DR-20 was raised for.
     Run 19 §15: measured on the whole order, gross, less the delivery fee —
     the same `minimumBasisGross` the minimum-order line reads, so the line
     and the block cannot say different things. */
  if (minimumBasisGross(totals.total) < minimum) {
    block.push(
      t(
        `A rendelés nem éri el a minimum rendelési értéket (${money(minimum)}).`,
        `The order is below the minimum order value (${money(minimum)}).`,
      ),
    )
  }

  /* §8.3, the short-notice lane */
  const short = calendar.ok && calendar.lane === 'short'
  if (short) {
    const left = context.short_notice.pieces_left
    if (left !== null && page.pieces > left) {
      enquiry.push(
        t(
          'rövid határidőre ennyi ételt csak egyeztetés után tudunk vállalni',
          'at short notice we can only take this much food after a conversation',
        ),
      )
    }
    /* Run 9, item 5 (DECIDED 20261010): a side with no gluten-free dish is
       never blocked or rerouted, "including at short notice; this replaces
       the enquiry route". The gluten-free card says which side has none. */
  }
  /* Fix list item 2 (Barna, 10 October 2026): a side with no gluten-free
     dish never blocks the order. Step 02 says which side has none. */

  /* §11.4, the six automatic reasons. Until `build/FIXES.md` item 81 (P2)
     this block was headed *review: paid first, then a person decides*, and
     Barna's ruling 2 of 8 October 2026 turns that round: **anything that
     needs a person is approved before payment.** The rules are unchanged --
     the same tests, the same parameters, the same figures each is measured on
     -- and what changed is where the answer goes.

     Both lists are filled: `automatic` carries the codes, which become
     confirmation reasons below, and `review` keeps the sentences it always
     kept, because §8.3 asks *did any of the six fire* and because nothing in
     this run deletes text. Neither list is ever shown to the customer. */
  const automaticReason = (code: ConfirmationReasonCode, hu: string, en: string): void => {
    automatic.push(code)
    review.push(t(hu, en))
  }
  if (page.guests > number('auto_accept_covers')) {
    automaticReason(
      'covers',
      `${number('auto_accept_covers')} fő felett`,
      `above ${number('auto_accept_covers')} guests`,
    )
  }
  if (totals.total.gross > number('review_value')) {
    automaticReason(
      'value',
      `${money(number('review_value'))} feletti érték`,
      `value above ${money(number('review_value'))}`,
    )
  }
  if (calendar.ok && calendar.clash) {
    automaticReason(
      'clash_date',
      'erre a napra már nagy rendezvényünk van',
      'we already have a large event that day',
    )
  }
  if (totals.total.delivery.review) automaticReason('zone_c', 'C zóna', 'zone C')
  /* The dietary note's own rule was here until run 7, as a §11.4 review
     reason. **Ruling 11(b) of 8 October 2026 moved it**, and the move is the
     one behavioural change the split makes to an existing code:
     `public.review_reason` keeps the value and its existing rows, and a new
     order writes `dietary_note` to `public.confirmation_reason` instead. It
     is raised with the confirmation reasons above.

     This is item 79's move, not F4's. **F4 stopped** — its precondition, that
     every dish card shows its allergens, is untrue of this catalogue
     (`scripts/allergen_precondition.mjs`) — so the free-text field, its hint
     and its wording all stand exactly as `081_page_questions` left them, and
     the sentence below is the one that was already there. */
  const softLeft = context.capacity.soft_portions_left
  /* Run 9, item 4 (DECIDED 20261010): *An order is never refused for its
     size.* Over portion_cap or max_covers_per_day the order is sent for
     confirmation with `capacity_over`, alongside `capacity_soft`, and the
     page knows it before sending -- so step 02 shows the usual confirmation
     note and no customer is refused after pressing the button. The server
     measures the same two figures (`submission.ts`) and `create_order`
     again under its lock. */
  const portionsLeft = context.capacity.portions_left ?? null
  const coversLeft = context.capacity.covers_left ?? null
  const capacityOver =
    (portionsLeft !== null && page.pieces > portionsLeft) ||
    (coversLeft !== null && page.guests > coversLeft)
  if ((softLeft !== null && page.pieces > softLeft) || capacityOver) {
    automaticReason('capacity_soft', 'a napi kapacitás közelében', "close to the day's capacity")
  }
  if (capacityOver) automaticReason('capacity_over', 'a napi kapacitás felett', "above the day's capacity")
  /* §8.3, unchanged by item 81 and pinned by a test: "an order inside the
     short-notice lane that carries any of the six reasons goes to the manual
     enquiry route (§8.3) and C5, exactly as today. Run 7's confirmation lane
     does not take it." So the five become confirmation reasons only off that
     lane; on it they stay what they have always been, the one enquiry reason
     that sends the submission to a colleague to ring back. */
  if (short && review.length > 0) {
    enquiry.push(t('rövid határidőn ellenőrzést igényel', 'needs a check at short notice'))
  } else {
    for (const code of automatic) confirmSilently(code)
  }

  /* `build/FIXES.md` item 79 (F8), *Too late to confirm*: "If the answer
     promise for this submission falls after the order's freeze point, the
     order cannot be sent for confirmation; show the existing late-order
     block."

     **The existing late-order block** is §11.3's own sentence for a date the
     page can no longer take an order for — the one `calendar.lane ===
     'closed'` already shows, and the one `submission.ts` answers with as
     `too_late`. The reading is logged in `DECISIONS_LOG.md`: a request nobody
     could answer in time is the same refusal as a date nobody can cook for,
     and this run writes no new copy.

     It applies only when a confirmation reason applies: an ordinary order
     waits for nobody, so no promise about it can be late. The promise itself
     is `public.confirmation_answer_due()`'s, carried on the context, because
     it is a walk over §8.1's business-day calendar and the page has the two
     times but not the days. A context that cannot promise an answer at all
     reads the same way as one whose promise is late — there is no answer to
     wait for either way. */
  if (confirmation.length > 0 && calendar.ok && calendar.freezeAt !== null) {
    const due = context.confirmation_answer_due ?? null
    const promised = due === null ? null : Date.parse(due)
    if (promised === null || !Number.isFinite(promised) || promised > calendar.freezeAt.getTime()) {
      block.push(
        t(
          'Erre a dátumra már nem tudunk rendelést felvenni.',
          'We can no longer take an order for this date.',
        ),
      )
    }
  }

  /* `build/FIXES.md` item 81 (P2): "review_reason is no longer written for new
     orders", so the page claims none either and agrees with what
     `submission.ts` sends and `public.orders_status_guard` enforces. The
     sentences above stay where they are; what is returned is the empty list. */
  return { block, review: [], enquiry, confirmation, short }
}

/* ------------------------------------------------------------------ *
 * §5.3: what the filters show
 * ------------------------------------------------------------------ */

export function dishesInFilter(args: {
  dishes: readonly MenuDish[]
  filter: Filter
  sub: string | null
  groupMembers: (groupKey: string) => Set<string>
}): MenuDish[] {
  const { dishes, filter, sub, groupMembers } = args
  const inSub = (dish: MenuDish): boolean =>
    sub === null || groupMembers(sub).has(dish.id)
  return dishes.filter((dish) => {
    if (filter === 'all') return true
    if (filter === 'sav') return dish.side === 'sav' && inSub(dish)
    if (filter === 'sw') return dish.side === 'sw' && inSub(dish)
    if (filter === 'gm') return dish.glutenFreeEligible
    return false
  })
}

/* ------------------------------------------------------------------ *
 * `build/FIXES.md` item 76 (F5): when there is an offer to show
 * ------------------------------------------------------------------ */

/**
 * *The offer appears only once the parameters needed to price it are set;
 * before that the card shows* Adja meg a rendezvény adatait…
 *
 * Item 76 does not say which parameters those are, so this is the one
 * clearly named function that decides it, chosen as the standing rule of 4
 * October asks — the simplest reading that keeps the build working — and
 * logged under *Defaults chosen in run7, for Barna*: an occasion, a headcount
 * the customer has actually typed, and a date the page could parse. The
 * other answers of step 01 — the profile, the balance, the staff package,
 * the allergies, the fulfilment — all open on a §6.1–§6.3 default, so none of
 * them can be unset and none of them can withhold the offer.
 *
 * Deliberately **not** `calendar.ok`: a holiday, a Monday, a clash or a date
 * past the freeze point is a priceable offer with a blocker on it, and
 * §11.3's own sentences are what say so. This asks only whether there is
 * something to price.
 */
export function offerIsReady(state: PageState, calendar: Calendar): boolean {
  if (state.occasion.trim() === '') return false
  const guests = Number(state.guestsField)
  if (state.guestsField.trim() === '' || !Number.isFinite(guests) || guests < 1) return false
  return calendar.eventDate !== null
}
